import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { request } from "@sharpana/auth/api_client/allauth";
import {
  page,
  card,
  title,
  subtitle,
  divider,
  metaRow,
  pill,
  cta,
  spinner,
  errorBox,
} from "../styles/postCheckoutStyles";

const GENERIC_PURCHASE_ERROR =
  "Seems like there was an issue with the purchase, please email us at support@mrble.ai and we will assist you promptly";

export default function StripePostCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const successParam = searchParams.get("success");
  const canceledParam = searchParams.get("canceled");
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] =
    useState<boolean>(false);
  const [tokensGranted, setTokensGranted] = useState<number>(0);

  useEffect(() => {
    const shouldConfirm = successParam === "true";
    if (!shouldConfirm) return;

    if (successParam === "true" && !sessionId) {
      setStatus("error");
      setErrorMsg(GENERIC_PURCHASE_ERROR);
      return;
    }

    let isCancelled = false;

    async function confirm() {
      try {
        setStatus("loading");
        const resp = await request(
          "POST",
          "/api/confirm-payment-grant-tokens-stripe",
          { session_id: sessionId }
        );
        if (isCancelled) return;

        if (resp && resp.success) {
          setHasActiveSubscription(!!resp.has_active_subscription);
          setTokensGranted(Number(resp.tokens_granted ?? 0));
          setStatus("success");
        } else {
          setStatus("error");
          setErrorMsg(GENERIC_PURCHASE_ERROR);
        }
      } catch (e) {
        if (isCancelled) return;
        setStatus("error");
        setErrorMsg(GENERIC_PURCHASE_ERROR);
      }
    }

    confirm();
    return () => {
      isCancelled = true;
    };
  }, [successParam, sessionId]);

  const isConfirmFlow = successParam === "true";
  const isCanceledFlow = canceledParam === "true";

  // Styles moved to ../styles/postCheckoutStyles

  const successTitle: React.ReactNode = hasActiveSubscription ? (
    <>
      Subscription purchase successful! <br /> <span>Welcome to Sharpana Plus</span>
    </>
  ) : (
    <>
      Purchase successful <br /> <span>You just bought {tokensGranted} credits, use them wisely!</span>
    </>
  );

  return (
    <div css={page}>
      <section css={card}>
        {(isCanceledFlow || !isConfirmFlow) && (
          <>
            <h1 css={title}>The transaction was cancelled.</h1>
            <p css={subtitle}>
              No charges were made. You can try again anytime.
            </p>
            <button
              css={cta}
              type="button"
              onClick={() => navigate("/purchase")}
            >
              Go back to Purchases
            </button>
          </>
        )}

        {isConfirmFlow && status === "loading" && (
          <>
            <div css={spinner} />
            <h1 css={title} style={{ marginTop: "1rem" }}>
              Processing your purchase…
            </h1>
            <p css={subtitle}>Please wait while we finalize your order.</p>
          </>
        )}

        {isConfirmFlow && status === "success" && (
          <>
            <h1 css={title}>{successTitle}</h1>
            <p css={subtitle}>
              Thanks for supporting Sharpana! You're all set.
            </p>
            <hr css={divider} />
            <div css={metaRow}>
              <span css={pill}>
                {hasActiveSubscription ? "Subscription" : "Credits"}
              </span>
              {!hasActiveSubscription && (
                <span css={pill}>{tokensGranted} tokens</span>
              )}
            </div>
            <button css={cta} onClick={() => navigate("/")}>
              Go Sharpen Up
            </button>
          </>
        )}

        {isConfirmFlow && status === "error" && (
          <>
            <h1 css={title}>There was an issue</h1>
            <div css={errorBox}>
              <p style={{ margin: 0 }}>{errorMsg}</p>
            </div>
            <div style={{ height: 12 }} />
            <button css={cta} onClick={() => navigate("/purchase")}>
              Try Again
            </button>
          </>
        )}
      </section>
    </div>
  );
}
